import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { toast } from "sonner";
import { 
  Building2, 
  Key, 
  Eye, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  DollarSign, 
  Sparkles, 
  Wrench, 
  Plus, 
  RefreshCw,
  Check
} from "lucide-react";

export const LandlordDashboard = () => {
  const { user, token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [propsRes, viewingsRes, matchesRes] = await Promise.all([
        api.get("/properties"),
        api.get("/viewings"),
        api.get("/matches")
      ]);
      setProperties(propsRes.data);
      setViewings(viewingsRes.data);
      setMatches(matchesRes.data);
    } catch (err) {
      console.error("Landlord fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user, token]);

  const handleUpdatePropertyStatus = async (propId, currentStatus) => {
    const nextStatus = currentStatus === "Active" ? "Viewing Underway" : currentStatus === "Viewing Underway" ? "Tenancy Agreed" : "Active";
    try {
      await api.patch(`/properties/${propId}`, { status: nextStatus });
      toast.success(`Property status updated to: ${nextStatus}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleConfirmViewing = async (viewingId) => {
    try {
      await api.patch(`/viewings/${viewingId}`, {
        status: "Confirmed",
        host_notes: "Confirmed by Landlord Host. Access code provided at reception."
      });
      toast.success("Viewing confirmed! Tenant and Concierge notified.");
      fetchData();
    } catch (e) {
      toast.error("Could not confirm viewing");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Welcome Header */}
        <div className="bg-[#091F5C] text-white rounded-2xl p-6 sm:p-8 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-emerald-900/80 border border-[#D0E4FE] text-[#D0E4FE] text-xs font-semibold">
              <Building2 className="w-3.5 h-3.5" />
              <span>Landlord Desk • Property Inventory</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold font-heading">
              {user?.company_name || user?.name || "Landlord Partner"}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
              Manage your London property inventory, review qualified tenant match inquiries, approve private viewings, and track preparation orders.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={fetchData}
              className="px-3.5 py-2 rounded-xl bg-[#091F5C] hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center space-x-1.5 border border-slate-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span>Refresh</span>
            </button>
            <Link
              to="/let"
              data-testid="landlord-add-property-btn"
              className="px-4 py-2.5 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-xs flex items-center space-x-1.5 shadow"
            >
              <Plus className="w-4 h-4" />
              <span>List New Property</span>
            </Link>
          </div>
        </div>

        {/* SECTION 1: Properties Portfolio */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
              <Key className="w-5 h-5 text-[#334DAF]" />
              <span>Registered Properties ({properties.length})</span>
            </h2>
          </div>

          {loading ? (
            <div className="p-8 rounded-2xl bg-white border border-[#E5EBF5] text-center text-xs text-[#4A5B78]">
              Loading properties...
            </div>
          ) : properties.length === 0 ? (
            <div className="p-8 rounded-2xl bg-white border border-[#E5EBF5] text-center space-y-3">
              <Building2 className="w-8 h-8 text-slate-400 mx-auto" />
              <p className="text-sm font-bold text-[#091F5C]">No properties listed yet</p>
              <p className="text-xs text-[#4A5B78]">Submit your available inventory to match with London tenant briefs.</p>
              <Link to="/let" className="inline-block px-5 py-2.5 rounded-xl bg-[#091F5C] text-white font-semibold text-xs">
                List Property Now →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((p) => (
                <div 
                  key={p.id}
                  data-testid={`landlord-property-card-${p.id}`}
                  className="bg-white rounded-2xl border border-[#E5EBF5] overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="h-44 overflow-hidden relative bg-[#E8F2FE]">
                      {p.images && p.images.length > 0 ? (
                        <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                          <Building2 className="w-8 h-8" />
                        </div>
                      )}
                      <div className="absolute top-2.5 left-2.5 bg-[#091F5C]/90 px-2.5 py-0.5 rounded-full text-[10px] font-bold text-white uppercase">
                        {p.borough}
                      </div>
                      <div className="absolute bottom-2.5 right-2.5 bg-white/95 px-2.5 py-1 rounded-lg text-xs font-bold text-[#091F5C] shadow">
                        £{p.rent_pcm?.toLocaleString()} PCM
                      </div>
                    </div>

                    <div className="p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-slate-400">{p.postcode}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                          p.status === "Active" 
                            ? "bg-[#D0E4FE] text-emerald-800" 
                            : p.status === "Viewing Underway" 
                            ? "bg-[#D0E4FE] text-blue-800"
                            : "bg-[#D0E4FE] text-purple-800"
                        }`}>
                          {p.status}
                        </span>
                      </div>

                      <h3 className="font-bold text-[#091F5C] text-sm font-heading line-clamp-1">
                        {p.title}
                      </h3>

                      <p className="text-xs text-[#4A5B78] line-clamp-2">
                        {p.description}
                      </p>

                      <div className="pt-2 border-t text-[11px] text-[#4A5B78] flex justify-between">
                        <span>{p.bedrooms} Beds • {p.bathrooms} Baths</span>
                        <span>Available: {p.available_from}</span>
                      </div>

                      {p.requested_prep_services && p.requested_prep_services.length > 0 && (
                        <div className="text-[10px] text-[#334DAF] bg-[#E8F2FE] px-2.5 py-1 rounded">
                          Prep Requested: {p.requested_prep_services.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-5 pt-0 border-t border-slate-100 mt-3">
                    <button
                      type="button"
                      onClick={() => handleUpdatePropertyStatus(p.id, p.status)}
                      data-testid={`toggle-prop-status-${p.id}`}
                      className="w-full mt-3 py-2 rounded-xl bg-[#E8F2FE] hover:bg-[#D0E4FE] text-[#091F5C] font-semibold text-xs transition-colors"
                    >
                      Cycle Status ({p.status})
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 2: Viewing Requests Inbox */}
        <div className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
              <Eye className="w-5 h-5 text-[#334DAF]" />
              <span>Incoming Viewing Requests ({viewings.length})</span>
            </h2>
          </div>

          {viewings.length === 0 ? (
            <div className="p-6 rounded-2xl bg-white border border-[#E5EBF5] text-center text-xs text-[#4A5B78]">
              No viewing requests from tenants yet. Concierge desk will push matched applicants here.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#E5EBF5] divide-y divide-slate-100 overflow-hidden shadow-sm">
              {viewings.map((v) => (
                <div key={v.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-[#091F5C] text-sm">{v.property_title}</span>
                      <span className="text-xs text-[#4A5B78]">• {v.property_borough}</span>
                    </div>
                    <div className="text-xs text-[#4A5B78]">
                      Applicant: <strong className="text-[#091F5C]">{v.tenant_name}</strong> ({v.tenant_email})
                    </div>
                    <div className="text-xs text-[#4A5B78] flex items-center space-x-3 pt-1">
                      <span>Requested Date: <strong>{v.preferred_date}</strong></span>
                      <span>Slot: <strong>{v.preferred_time_slot}</strong></span>
                    </div>
                    {v.comments && (
                      <p className="text-[11px] text-[#4A5B78] italic">"{v.comments}"</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {v.status === "Confirmed" ? (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D0E4FE] text-emerald-800 border border-[#D0E4FE] flex items-center space-x-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Confirmed</span>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleConfirmViewing(v.id)}
                        data-testid={`confirm-viewing-btn-${v.id}`}
                        className="px-4 py-2 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-xs flex items-center space-x-1 shadow"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>Confirm Viewing</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 3: Concierge Matched Tenant Leads */}
        <div className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span>Matched Tenant Leads Dispatched by Concierge ({matches.length})</span>
            </h2>
          </div>

          <div className="bg-white rounded-2xl border border-[#E5EBF5] divide-y divide-slate-100 overflow-hidden shadow-sm">
            {matches.map((m) => (
              <div key={m.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-[#E8F2FE] text-[#334DAF]">
                      {m.similarity_tag || "Matched by Concierge"}
                    </span>
                    <span className="text-sm font-bold text-[#091F5C]">{m.property_title}</span>
                  </div>
                  <div className="text-xs text-[#4A5B78]">
                    Tenant Applicant: <span className="font-semibold">{m.tenant_name || "Verified London Renter"}</span>
                  </div>
                  <p className="text-xs text-[#4A5B78] italic">
                    Concierge Pairing Note: "{m.concierge_notes}"
                  </p>
                </div>
                <div>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#E8F2FE] text-[#4A5B78]">
                    {m.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};
